import {
  chatSummaryUpdatedDossbotEventDataSchema,
  chatTitleUpdatedDossbotEventDataSchema,
} from '@doss/common-all'
import { flip, offset, useClientPoint, useFloating, useInteractions } from '@floating-ui/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { MdClose, MdOpenInFull } from 'react-icons/md'
import { useNavigate } from 'react-router'
import { Button } from '~/@/components/ui/button'
import { apolloClient } from '~/common/apollo-client'
import {
  DossbotEventType,
  usePushDossbotEventSubscription,
} from '~/common/generated/apollo-operations'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import {
  retrieveFloatingWidgetCorner,
  storeFloatingWidgetCorner,
} from '~/common/utils/local-storage'
import { AnimatedDossbotIcon } from '~/components/dossbot/AnimatedDossbotIcon'
import { DossbotChatPanel } from '~/components/dossbot/DossbotChatPanel'

type Corner = 'bottom-end' | 'bottom-start'

const DRAG_THRESHOLD_PIXELS = 5

/**
 * FloatingChatWidget component that allows the user to open the Doss AI chat widget
 */
export function FloatingChatWidget() {
  const [isOpen, setIsOpen] = useState(false)
  const [chatId, setChatId] = useState<string | null>(null)
  const { user } = useGlobalContext()
  const [isDragging, setIsDragging] = useState(false)
  const [corner, setCorner] = useState<Corner>('bottom-end')
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const dragStartPosRef = useRef<{ x: number; y: number } | null>(null)

  const createScreenReference = useCallback(() => {
    const width = typeof window !== 'undefined' ? window.innerWidth : 0
    const height = typeof window !== 'undefined' ? window.innerHeight : 0
    return {
      getBoundingClientRect() {
        return {
          width,
          height,
          x: 0,
          y: 0,
          top: 0,
          left: 0,
          right: width,
          bottom: height,
        }
      },
    }
  }, [])

  const [screenReference, setScreenReference] = useState(() => createScreenReference())
  // On mobile when open, use fixed positioning. Otherwise use floating-ui
  const useFixedPositioning = isMobile && isOpen
  const mainAxisOffset = isOpen ? -540 : -70
  const { refs, floatingStyles, context, isPositioned } = useFloating({
    placement: corner,
    middleware: [
      offset({ mainAxis: isDragging ? -15 : mainAxisOffset, alignmentAxis: isDragging ? -20 : 30 }),
      flip(),
    ],
  })
  const clientPoint = useClientPoint(context, {
    enabled: isDragging && !useFixedPositioning && !isMobile,
  })
  const { getFloatingProps } = useInteractions([clientPoint])

  useEffect(() => {
    if (user?.id) {
      const storedCorner = retrieveFloatingWidgetCorner(user.id)
      if (storedCorner && ['bottom-end', 'bottom-start'].includes(storedCorner)) {
        setCorner(storedCorner as Corner)
      }
    }
  }, [user])

  useEffect(() => {
    setScreenReference(createScreenReference())
  }, [corner, createScreenReference, isOpen])

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(typeof window !== 'undefined' && window.innerWidth < 640) // sm breakpoint
    }
    checkMobile()

    const handleResize = () => {
      setShouldAnimate(false)
      checkMobile()
      setScreenReference(createScreenReference())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [corner, createScreenReference, isOpen])

  const getClosestCorner = (x: number): typeof corner => {
    const windowWidth = window.innerWidth

    const isLeft = x < windowWidth / 2

    return isLeft ? 'bottom-start' : 'bottom-end'
  }

  useEffect(() => {
    if (!isDragging && !useFixedPositioning) {
      refs.setPositionReference(screenReference)
    }
  }, [refs, screenReference, isDragging, useFixedPositioning])

  const navigate = useNavigate()

  usePushDossbotEventSubscription({
    variables: { chatId: chatId ?? '' },
    onData: ({ data }) => {
      if (!data.data) {
        return
      }

      const event = data.data.pushDossbotEvent

      switch (event.type) {
        case DossbotEventType.CHAT_SUMMARY_UPDATED: {
          const parsedEvent = chatSummaryUpdatedDossbotEventDataSchema.parse(event.payload)
          apolloClient.cache.modify({
            id: `DossbotChat:${chatId}`,
            fields: {
              summary: () => parsedEvent.summary,
            },
          })
          break
        }
        case DossbotEventType.CHAT_TITLE_UPDATED: {
          const parsedEvent = chatTitleUpdatedDossbotEventDataSchema.parse(event.payload)
          apolloClient.cache.modify({
            id: `DossbotChat:${chatId}`,
            fields: {
              title: () => parsedEvent.title,
            },
          })
          break
        }
        case DossbotEventType.CHAT_RUN_STARTED:
        case DossbotEventType.CHAT_RUN_COMPLETED:
        case DossbotEventType.CHAT_RUN_FAILED:
        case DossbotEventType.CHAT_RUN_CANCELED:
        case DossbotEventType.TOOL_RUN_STARTED:
        case DossbotEventType.TOOL_RUN_COMPLETED:
        case DossbotEventType.TOOL_RUN_FAILED:
        case DossbotEventType.TOOL_RUN_CANCELED:
        case DossbotEventType.TOOL_RUN_PENDING:
        case DossbotEventType.TOOL_RUN_APPROVED:
        case DossbotEventType.TOOL_RUN_REJECTED:
        case DossbotEventType.AGENT_RUN_COMPLETED:
        case DossbotEventType.AGENT_RUN_STARTED:
        case DossbotEventType.AGENT_RUN_FAILED:
        case DossbotEventType.AGENT_RUN_CANCELED:
          break
      }
    },
  })

  const handleExpand = () => {
    if (chatId) {
      navigate(`/dossbot/chat/${chatId}`)
      setIsOpen(false)
    }
  }

  const handleChatSwitch = (selectedChatId: string) => {
    setChatId(selectedChatId)
  }

  const handleCreateNewChat = () => {
    setChatId(null)
  }

  const handleOpenWidget = () => {
    if (!isDragging) {
      setShouldAnimate(false)
      setIsOpen(true)
    }
  }

  const handlePointerMove = (e: PointerEvent) => {
    e.preventDefault()
    setIsDragging(true)
    refs.setPositionReference({
      getBoundingClientRect() {
        return {
          width: 0,
          height: 0,
          x: e.clientX,
          y: e.clientY,
          top: e.clientY,
          left: e.clientX,
          right: e.clientX,
          bottom: e.clientY,
        }
      },
    })
  }

  const handlePointerUp = (e: PointerEvent) => {
    e.preventDefault()
    e.stopPropagation()

    // Check if movement was small enough to be considered a click/tap
    if (dragStartPosRef.current) {
      const deltaX = Math.abs(e.clientX - dragStartPosRef.current.x)
      const deltaY = Math.abs(e.clientY - dragStartPosRef.current.y)
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)

      if (distance <= DRAG_THRESHOLD_PIXELS && !isOpen) {
        // Small movement - treat as click/tap and open widget
        handleOpenWidget()
      } else if (distance > DRAG_THRESHOLD_PIXELS) {
        // Significant movement - treat as drag and update corner position
        const closestCorner = getClosestCorner(e.clientX)
        setCorner(closestCorner)
        if (user?.id) {
          storeFloatingWidgetCorner(user.id, closestCorner)
        }
      }
    }

    // Clean up
    setTimeout(() => setIsDragging(false), 1)
    dragStartPosRef.current = null
    document.removeEventListener('pointermove', handlePointerMove)
    document.removeEventListener('pointerup', handlePointerUp)
    document.removeEventListener('pointercancel', handlePointerUp)
  }

  const handlePointerDown = (e: React.PointerEvent) => {
    // Don't allow dragging on mobile when widget is open
    if (isMobile && isOpen) {
      return
    }
    setShouldAnimate(true)
    e.preventDefault()
    e.stopPropagation()

    // Initialize drag tracking
    dragStartPosRef.current = { x: e.clientX, y: e.clientY }
    // Capture the pointer so iframes (e.g. dynamic app guest-frame embeds) under the cursor can't swallow the drag.
    // Captured events retarget to this element and still bubble to the document listeners below.
    // Capture is released implicitly on pointerup/pointercancel.
    e.currentTarget.setPointerCapture(e.pointerId)
    document.addEventListener('pointermove', handlePointerMove)
    document.addEventListener('pointerup', handlePointerUp)
    document.addEventListener('pointercancel', handlePointerUp)
  }

  if (!isOpen) {
    return (
      <div
        ref={refs.setFloating}
        className="fixed z-[1400]"
        onPointerDown={handlePointerDown}
        style={{
          ...floatingStyles,
          transition: shouldAnimate ? 'all .2s linear' : 'none',
          visibility: isPositioned ? 'visible' : 'hidden',
        }}
        {...(!isMobile ? getFloatingProps() : {})}
      >
        <Button
          aria-label="Open Dossbot Chat"
          className="group size-10 rounded-full border border-gray-5 bg-gray-3 shadow-lg transition-all duration-200 hover:scale-105 hover:bg-gray-4 hover:shadow-xl focus:ring-4 focus:ring-gray-3"
          onClick={handleOpenWidget}
          size="icon"
          variant="ghost"
        >
          <AnimatedDossbotIcon
            animate={false}
            className="pointer-events-none transition-transform duration-200 group-hover:scale-110"
            height={24}
            width={24}
          />
        </Button>
      </div>
    )
  }

  // On mobile when open, use fixed positioning instead of floating-ui
  const mobileStyles = useFixedPositioning
    ? {
        position: 'fixed' as const,
        bottom: 0,
        left: 0,
        right: 0,
        top: 'auto',
      }
    : floatingStyles

  return (
    <div
      ref={refs.setFloating}
      className="z-[1400] flex h-[32rem] w-full flex-col overflow-hidden rounded-t-xl border border-gray-2 bg-white shadow-2xl sm:w-[450px] sm:rounded-xl"
      style={mobileStyles}
      {...(useFixedPositioning ? {} : getFloatingProps())}
    >
      <DossbotChatPanel
        actions={
          <>
            {chatId && !chatId.startsWith('temp-') && (
              <Button
                aria-label="Expand to full page"
                className="size-8"
                onClick={handleExpand}
                size="icon"
                title="Open in full page"
                variant="ghost"
              >
                <MdOpenInFull size={16} />
              </Button>
            )}
            <Button
              aria-label="Close chat"
              className="size-8"
              onClick={() => {
                setShouldAnimate(false)
                setIsOpen(false)
              }}
              size="icon"
              title="Close chat"
              variant="ghost"
            >
              <MdClose size={16} />
            </Button>
          </>
        }
        chatId={chatId}
        compact
        onNewChat={handleCreateNewChat}
        onSelectChat={handleChatSwitch}
      />
    </div>
  )
}
